import React, { useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogHeader } from "./ui/dialog";

const isMac = () => {
	if (typeof navigator === "undefined") return false;
	return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
};

const getModifierKey = () => (isMac() ? "⌘" : "Strg");
const getShiftKey = () => (isMac() ? "⇧" : "Umschalt");
const getAltKey = () => (isMac() ? "⌥" : "Alt");

export const JobItemsShortcutsDialog: React.FC<{
	open: boolean;
	onClose: () => void;
}> = ({ open, onClose }) => {
	const mod = useMemo(() => getModifierKey(), []);
	const shift = useMemo(() => getShiftKey(), []);
	const alt = useMemo(() => getAltKey(), []);

	return (
		<Dialog open={open} onOpenChange={(open: boolean) => !open && onClose()}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Tastenkürzel für Auftragspositions-Tabelle</DialogTitle>
				</DialogHeader>
				<div className="space-y-6">
					<div className="grid grid-cols-2 gap-6">
						<div>
							<Section title="Im Dropdown">
								<Shortcut keys="↑ / ↓" description="Zwischen Optionen wechseln" />
								<Shortcut keys={`${mod} + ↑`} description="Zur ersten Option" />
								<Shortcut keys={`${mod} + ↓`} description="Zur letzten Option" />
								<Shortcut keys="↵" description="Option übernehmen" />
								<Shortcut keys="Esc" description="Abbrechen" />
								<Shortcut keys="⌫" description="Techniker entfernen" />
							</Section>
						</div>

						<div>
							<Section title="Tabellen-Navigation">
								<Shortcut keys="↑ ↓ ← →" description="Zwischen Zellen wechseln" />
								<Shortcut keys="⇥" description="Zum nächsten Feld" />
								<Shortcut keys={`${shift} + ⇥`} description="Zum vorherigen Feld" />
								<Shortcut keys={`${mod} + ↑ ↓ ← →`} description="Zum Tabellenrand springen" />
							</Section>
						</div>
					</div>

					<div className="border-t pt-5">
						<Section title="Mehrfach-Zuweisung · Wirkt nur auf Positionen der gleichen Art">
							<div className="grid grid-cols-2 gap-x-6 gap-y-2">
								<Shortcut keys={`${mod} + ↵`} description="Ab dieser Position bis Ende" />
								<Shortcut keys={`${shift} + ↵`} description="Von erster Position bis hier" />
								<Shortcut keys={`${alt} + ↵`} description="Auf alle Positionen anwenden" />
							</div>
						</Section>
					</div>

					<div className="border-t pt-5">
						<Section title="Rückgängig machen">
							<div className="grid grid-cols-2 gap-x-6 gap-y-2">
								<Shortcut keys={`${mod} + Z`} description="Letzte Änderung rückgängig" />
								<Shortcut keys={`${mod} + ${shift} + Z`} description="Änderung wiederherstellen" />
							</div>
						</Section>
					</div>

					<div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground space-y-2">
						<p>
							<span className="font-medium text-foreground">Positionsart:</span> Tarif,
							Material, Porto, etc.
						</p>
						<p>
							<span className="font-medium text-foreground">Verlauf:</span> Das System
							speichert die letzten 20 Schritte. Eine Mehrfach-Zuweisung zählt als ein Schritt.
						</p>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<div className="space-y-3">
		<h3 className="font-semibold text-sm text-foreground">{title}</h3>
		<div className="space-y-2">{children}</div>
	</div>
);

const Shortcut: React.FC<{ keys: string; description: string }> = ({ keys, description }) => (
	<div className="flex items-center gap-3 text-sm">
		<kbd className="font-mono text-base bg-background px-2 py-1.5 rounded border shadow-sm whitespace-nowrap min-w-[140px] text-center">
			{keys}
		</kbd>
		<span className="text-foreground/80">{description}</span>
	</div>
);
